import { useState } from "react";
import {
  Checkbox,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import type { ColDef } from "ag-grid-community";
import "@/assets/css/tabs.css";
import {
  MEETING_FIELDS,
  PIPELINE_VIEW_ORDER,
  PIPELINE_VIEWS,
  type PipelineViewId,
} from "../pipelineViews";

type PipelineViewTabsProps = {
  view: PipelineViewId;
  setView: (view: PipelineViewId) => void;
  customColumns: string[];
  setCustomColumns: (fields: string[]) => void;
  /** All Pipeline columns, to choose Custom columns from. */
  columns: ColDef[];
};

/**
 * Column presets as the app's standard tabs (same as Summary). Clicking the
 * Custom tab while it's selected opens its column chooser.
 */
export function PipelineViewTabs({
  view,
  setView,
  customColumns,
  setCustomColumns,
  columns,
}: PipelineViewTabsProps) {
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);

  const choosable = columns
    .map((col) => ({
      key: col.colId ?? col.field ?? "",
      label: col.headerName ?? col.field ?? "",
    }))
    .filter((col) => col.key && col.key !== "deal_name");
  const selected = customColumns.length > 0 ? customColumns : MEETING_FIELDS;

  const toggleColumn = (key: string) => {
    const next = selected.includes(key)
      ? selected.filter((field) => field !== key)
      : [...selected, key];
    // The deal name always leads so rows stay identifiable.
    setCustomColumns(["deal_name", ...next.filter((f) => f !== "deal_name")]);
  };

  return (
    <>
      <Tabs
        value={view}
        onChange={(_, next: PipelineViewId) => setView(next)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Pipeline views"
        TabIndicatorProps={{ className: "tf-tabs__indicator" }}
        sx={{
          minHeight: 0,
          "& .MuiTabs-flexContainer": { alignItems: "center" },
          "& .MuiTabScrollButton-root": { width: 15 },
          "& .MuiTab-root.tf-tab.tf-tab--compact": {
            minHeight: "43px !important",
            py: 1,
          },
        }}
      >
        {PIPELINE_VIEW_ORDER.map((id) => (
          <Tab
            key={id}
            value={id}
            label={
              <Tooltip title={PIPELINE_VIEWS[id].description} enterDelay={400}>
                <span>{PIPELINE_VIEWS[id].label}</span>
              </Tooltip>
            }
            className="tf-tab tf-tab--compact"
            onClick={(e) => {
              if (id === "custom" && view === "custom") {
                setColumnsAnchor(e.currentTarget);
              }
            }}
          />
        ))}
      </Tabs>

      <Menu
        anchorEl={columnsAnchor}
        open={columnsAnchor != null}
        onClose={() => setColumnsAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 480, minWidth: 240 } } }}
      >
        <MenuItem dense disabled>
          <ListItemText
            primary="Choose columns · Deal Name is always shown"
            slotProps={{ primary: { fontSize: 11 } }}
          />
        </MenuItem>
        <Divider />
        {choosable.map((col) => (
          <MenuItem key={col.key} dense onClick={() => toggleColumn(col.key)}>
            <Checkbox
              size="small"
              checked={selected.includes(col.key)}
              sx={{ p: 0.25, mr: 1 }}
            />
            <ListItemText
              primary={col.label}
              slotProps={{ primary: { fontSize: 12 } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
